import React, { useMemo, useState, useRef, useEffect } from 'react';

// ── Sentence machinery ──────────────────────────────────────────────────────
const ABBR_MARK = '\x01';

function protectAbbrevs(text) {
  let t = text.replace(
    /\b(Mr|Mrs|Dr|St|etc|vs|No|Jr|Sr|Messrs|Inc|Corp|Ltd|Co|Bros|Sen|Rep|Gov|Gen|Rev|Hon|Prof|Ave|Dept|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\./g,
    (_, g) => g + ABBR_MARK
  );
  t = t.replace(/\b([ie])\. ?([ge])\./gi, (_, g1, g2) => g1 + ABBR_MARK + g2 + ABBR_MARK);
  t = t.replace(/\b([A-Z])\./g, (_, g) => g + ABBR_MARK);
  return t;
}

function splitSentences(para) {
  const t = protectAbbrevs(para);
  const RE = /[.?!]+["\u2019\u201d')\]]*\s+/g;
  const out = [];
  let pos = 0, m;
  while ((m = RE.exec(t)) !== null) {
    const end = m.index + m[0].length;
    const seg = t.slice(pos, end).trim();
    const nxt = t[end] || '';
    if (seg && (!nxt || /[A-Z]/.test(nxt) || '"\u2018\u2019\u201c\u201d\'(0123456789'.includes(nxt))) {
      out.push(seg);
      pos = end;
    }
  }
  const last = t.slice(pos).trim();
  if (last) out.push(last);
  return out.map(s => s.replace(new RegExp(ABBR_MARK, 'g'), '.')).filter(s => s.trim());
}

const STOPWORDS = new Set(['their','there','which','would','could','should','about',
  'these','those','thing','things','people','every','being','because','before',
  'after','other','another','between','that','this','with','from','have','been',
  'were','what','when','where','they','them','then','than','some','into']);

function contentWords(text) {
  const ws = new Set();
  for (const w of (text.toLowerCase().match(/[a-z]{4,}/g) || [])) {
    if (!STOPWORDS.has(w)) ws.add(w);
  }
  return ws;
}

function setIntersects(a, b) {
  for (const v of a) if (b.has(v)) return true;
  return false;
}

const ANAPHORA_RE = /^(?:sometimes|often|usually|always|now|here|there|also|still|even|yes|no)?\s*(this|that|these|those|it|its|he|she|they|and|indeed|nor|neither|either|the same|such|so much|at any rate|in fact|anyway|none of us|we all|all of us|you and i)\b/i;

function stripOpener(sent) {
  return sent.replace(/^["\u2018\u2019\u201c\u201d([\]… ]+/, '');
}

const Q = '"\'\u2018\u2019\u201c\u201d';
const _HEDGE = '(?:\\s+(?:now|then|here|also|again|really|simply|just|merely|usually|normally|generally|originally|properly|strictly|literally|roughly|nowadays|today|[a-z]+ly)){0,3}';
const _DEF_MEANS_RE = new RegExp(
  '^\\s*(?<subj>[' + Q + '][^' + Q + ']{1,40}[' + Q + ']' +
  '|the\\s+(?:word|term|name|phrase|expression)\\s+[' + Q + ']?[\\w-]{1,30}[' + Q + ']?' +
  '|[A-Za-z][\\w-]{1,30}[' + Q + ']' +
  '|[A-Za-z][A-Za-z-]{1,30})' +
  _HEDGE +
  '\\s+mean(?:s|t)\\b\\s*(?<obj>.*)$', 'i'
);
const BAD_SUBJ = new Set(['i','he','she','we','they','you','who','one','someone','somebody',
  'anyone','everyone','everybody','nobody','that','this','these','those','there','what',
  'which','god','christ','jesus','man','people','no']);
const BAD_OBJ_RE = /^(?:that\b|to\b|of\b|as\b|for\b|in\b|with\b|by\b|from\b|(?:i|he|she|we|they|you|it|there|one)\b|(?:both|well|business|mischief|harm|so|otherwise|nothing|everything|anything)\b)/i;

function isDefinition(sentence) {
  const m = _DEF_MEANS_RE.exec(sentence.trim());
  if (!m) return false;
  const subj = m.groups.subj.trim();
  const core = subj.replace(new RegExp('[' + Q + ']', 'g'), '').toLowerCase();
  const mentioned = Q.includes(subj[0]) || Q.includes(subj[subj.length - 1]) || core.includes(' ');
  if (!mentioned && BAD_SUBJ.has(core)) return false;
  const obj = (m.groups.obj || '').replace(/^[\s,;:\u2014-]+/, '');
  if (BAD_OBJ_RE.test(obj)) return false;
  return true;
}

// ── Flat (news) mode ─────────────────────────────────────────────────────────
const FLAT_INDENT_RULES = [
  ['evidence', /^(because|for\b|since\b|after all|the reason|in fact|as a matter of fact|the fact (?:is|remains)|we know|it is a fact|that is (?:the reason|why we know))/i],
  ['example',  /^(for example|for instance|take |think of |consider |suppose, for example)/i],
  ['contrast', /^((?:so )?far from|but|however|yet\b|nevertheless|none the less|nonetheless|still\b|on the other hand|on the contrary|whereas|while\b(?!\s+it\s+is\s+true)|conversely|in contrast|at the same time)/i],
];

function flatTagSentence(s) {
  const stripped = stripOpener(s);
  for (const [tag, rx] of FLAT_INDENT_RULES) {
    if (rx.test(stripped)) return tag;
  }
  return null;
}

function flatChapter(paragraphs) {
  const roots = [];
  let lastRoot = null;
  for (const para of paragraphs) {
    if (roots.length > 0) roots.push(new OutlineNode('', 'spacer'));
    const sents = splitSentences(para);
    for (const s of sents) {
      const tag = flatTagSentence(s);
      if (tag && lastRoot) {
        lastRoot.children.push(new OutlineNode(s, tag));
      } else {
        const node = new OutlineNode(s);
        roots.push(node);
        lastRoot = node;
      }
    }
  }
  return roots;
}

// ── Theology tagger ─────────────────────────────────────────────────────────
const TAG_RULES = [
  ['concession', /^(of course|no doubt|doubtless|admittedly|it is true|certainly|to be sure|i know|i admit|you may (?:say|think|ask|object|feel)|some (?:people|one|of you) (?:may |might |will )?(?:say|think|ask|object|feel)|it may be (?:said|thought|urged|objected)|we (?:may|might) be told|it is (?:often|sometimes) (?:said|thought))/i],
  ['example', /^(for example|for instance|take |think of |consider |suppose, for example)/i],
  ['analogy', /^(just as|it is as if|as if|imagine|suppose|like |similarly|in the same way)/i],
  ['contrast', /^((?:so )?far from|but|however|yet\b|nevertheless|none the less|nonetheless|still\b|on the other hand|on the contrary|whereas|while\b(?!\s+it\s+is\s+true)|conversely|in contrast|at the same time)/i],
  ['evidence', /^(because|for\b|since\b|after all|the reason|in fact|as a matter of fact|the fact (?:is|remains)|we know|it is a fact|that is (?:the reason|why we know))/i],
  ['qualification', /^(?:now\s+)?(if\b|unless|provided|although|though\b|even if|even though|when\b|whenever|as long as|so long as|as far as|so far as|while it is true|only\b)/i],
  ['consequence', /^(therefore|thus|hence|consequently|accordingly|so\b(?!\s+(?:long|far|much|many|great|be\b))|then\b|it follows|which means|that is why|the result is|and so|in that case)/i],
  ['restatement', /^(that is\b|in other words|i mean|or rather|namely|again\b|to put it (?:another way|differently)|put another way|(?:first|firstly|second|secondly|third|thirdly|fourth|fourthly|fifth|finally|lastly|next\b)[,.)])/i],
  ['definition', /^(by .{1,40} i mean|what (?:do )?(?:we|i) mean|let me (?:define|explain)|i am using|we mean by|i mean by)/i],
];

function tagSentence(sent) {
  const s = stripOpener(sent);
  for (const [tag, rx] of TAG_RULES) {
    if (rx.test(s)) return tag;
  }
  return null;
}

// ── Tree node ────────────────────────────────────────────────────────────────
class OutlineNode {
  constructor(text, tag = null) {
    this.text = text;
    this.tag = tag;
    this.children = [];
  }
}

// ── Outliner ─────────────────────────────────────────────────────────────────
function outlineChapter(paragraphs) {
  const roots = [];
  for (const para of paragraphs) {
    const sents = splitSentences(para);
    if (!sents.length) continue;
    const claim = new OutlineNode(sents[0]);
    roots.push(claim);
    let prev = claim, prevDepth = 1;
    const deferredDefs = [];
    const heavyQuote = sents.filter(s => s[0] === '"' || s[0] === '\u201c').length >= Math.max(2, sents.length >> 1);
    for (const s of sents.slice(1)) {
      const stripped = stripOpener(s);
      let tag = tagSentence(s);
      if (heavyQuote && !tag) tag = 'example';
      if (tag) {
        const node = new OutlineNode(s, tag);
        claim.children.push(node);
        prev = node; prevDepth = 2;
        continue;
      }
      const ow = contentWords(s);
      const prevText = prev.text.trimEnd();
      if (prevText[prevText.length - 1] === ':') {
        const node = new OutlineNode(s, 'example');
        const parent = prevDepth < 4 ? prev : claim;
        parent.children.push(node);
        prev = node; prevDepth = Math.min(prevDepth + 1, 4);
      } else if (isDefinition(stripped)) {
        const node = new OutlineNode(s, 'definition');
        deferredDefs.push(node);
        prev = node; prevDepth = 2;
      } else if (ANAPHORA_RE.test(stripped) || setIntersects(ow, contentWords(prev.text)) || setIntersects(ow, contentWords(claim.text))) {
        const node = new OutlineNode(s, 'restatement');
        const parent = (prev !== claim && prevDepth < 4) ? prev : claim;
        parent.children.push(node);
        prev = node; prevDepth = Math.min((parent === prev ? prevDepth : 1) + 1, 4);
      } else {
        roots.push(new OutlineNode(s, null));
        prev = claim; prevDepth = 1;
      }
    }
    claim.children.push(...deferredDefs);
  }
  return roots;
}

// ── Tag colours ──────────────────────────────────────────────────────────────
const TAG_STYLES = {
  evidence:      { bg: '#e2efe0', color: '#3c7a3c' },
  definition:    { bg: '#ece2f0', color: '#6d4a8a' },
  qualification: { bg: '#f7e8d4', color: '#a05a16' },
  example:       { bg: '#dcefee', color: '#2c7a76' },
  analogy:       { bg: '#ddeaf5', color: '#2e6da4' },
  concession:    { bg: '#f5dddd', color: '#a03a3a' },
  consequence:   { bg: '#dfe3f5', color: '#3a4aa0' },
  contrast:      { bg: '#fbe9d5', color: '#b3641a' },
  restatement:   { bg: '#eceae4', color: '#6b675c' },
};

const INDENT_TAGS = new Set(['qualification', 'consequence', 'contrast']);

// ── React tree renderer ──────────────────────────────────────────────────────
function OutlineNodeEl({ node, showTags, gameWordIdx, nodeWordRanges, activeWordRef, onNodeRef, typeNodes, typeProps, onHoverNode }) {
  if (node.tag === 'spacer') {
    return <li style={{ listStyle: 'none', margin: '10px 0 2px', minHeight: 10 }} aria-hidden="true" />;
  }
  const ts = node.tag ? TAG_STYLES[node.tag] : null;
  const extraIndent = node.tag && INDENT_TAGS.has(node.tag) ? 16 : 0;

  // Word-hop game: render words as individually addressable spans
  let textContent;
  const range = nodeWordRanges?.get(node);
  if (range && gameWordIdx !== null) {
    const tokens = node.text.split(/(\s+)/);
    let wordCounter = 0;
    textContent = tokens.map((token, ti) => {
      if (/^\s+$/.test(token)) return token;
      const globalIdx = range.start + wordCounter++;
      const isActive = globalIdx === gameWordIdx;
      return (
        <span
          key={ti}
          ref={isActive ? activeWordRef : null}
          style={isActive ? { background: '#3b82f6', color: '#fff', borderRadius: 3, padding: '0 2px', fontWeight: 'bold' } : {}}
        >{token}</span>
      );
    });
  } else {
    textContent = node.text;
  }

  return (
    <li ref={el => { if (el && onNodeRef) onNodeRef(node, el); }} onMouseOver={onHoverNode ? (e) => { e.stopPropagation(); onHoverNode(node); } : undefined} style={{ margin: '4px 0', lineHeight: 1.55, position: 'relative', paddingLeft: 16, marginLeft: extraIndent }}>
      <span style={{ position: 'absolute', left: 0, top: '0.65em', width: 5, height: 5, borderRadius: '50%', background: '#b9b2a2', display: 'inline-block' }} />
      {textContent}
      {showTags && node.tag && ts && (
        <span style={{ fontFamily: 'monospace', fontSize: 10, padding: '1px 5px', borderRadius: 9, marginLeft: 6, background: ts.bg, color: ts.color, whiteSpace: 'nowrap' }}>
          [{node.tag}]
        </span>
      )}
      {typeNodes?.has(node) && typeProps && (
        <div style={{ marginTop: 6 }}>
          <TypewriterLine text={node.text} label="" {...typeProps} />
        </div>
      )}
      {node.children.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, paddingLeft: 22 }}>
          {node.children.map((child, i) => (
            <OutlineNodeEl key={i} node={child} showTags={showTags} gameWordIdx={gameWordIdx} nodeWordRanges={nodeWordRanges} activeWordRef={activeWordRef} onNodeRef={onNodeRef} typeNodes={typeNodes} typeProps={typeProps} onHoverNode={onHoverNode} />
          ))}
        </ul>
      )}
    </li>
  );
}

// ── AI outline parser ────────────────────────────────────────────────────────
function parseAIOutline(text) {
  const lines = text.split('\n').filter(l => l.trim());
  const roots = [];
  const stack = []; // [{node, depth}]

  for (const line of lines) {
    const spaces = (line.match(/^(\s*)/) || ['', ''])[1].length;
    const depth = Math.floor(spaces / 2);
    const m = line.trim().match(/^-\s+(\S+)\s+\[([^\]]+)\]\s+(.*)/);
    if (!m) continue;
    const [, addr, rawTag, text] = m;
    // strip compound claim prefix for display tag
    const tag = rawTag === '--' ? null : rawTag.replace(/^claim\+/, '');
    const node = new OutlineNode(`${addr}  ${text}`, tag);

    if (depth === 0) {
      roots.push(node);
      stack.length = 0;
      stack.push({ node, depth: 0 });
    } else {
      while (stack.length > 0 && stack[stack.length - 1].depth >= depth) stack.pop();
      const parent = stack.length > 0 ? stack[stack.length - 1].node : null;
      if (parent) parent.children.push(node); else roots.push(node);
      stack.push({ node, depth });
    }
  }
  return roots;
}

// ── Main Modal ───────────────────────────────────────────────────────────────
// ── Typewriter widget: types out text, then leaves a blinking caret ──────────────
function TypewriterLine({ text, label, isDarkMode, accentColor, borderColor }) {
  const [shown, setShown] = useState('');
  const [typing, setTyping] = useState(false);
  const [replayKey, setReplayKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setShown('');
    if (!text) return undefined;
    setTyping(true);
    (async () => {
      const sleep = (ms) => new Promise(r => setTimeout(r, ms));
      await sleep(600);
      for (let i = 1; i <= text.length; i++) {
        if (cancelled) return;
        setShown(text.slice(0, i));
        await sleep(25 + Math.random() * 45);
      }
      if (!cancelled) setTyping(false);
    })();
    return () => { cancelled = true; };
  }, [text, replayKey]);

  if (!text) return null;
  return (
    <div style={{ marginBottom: 18 }}>
      <style>{'@keyframes outline-caret-blink { 50% { opacity: 0; } }'}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 11, color: accentColor, letterSpacing: '0.04em' }}>{label}</span>
        <button
          onClick={() => setReplayKey(k => k + 1)}
          title="Replay typing"
          style={{ fontFamily: 'monospace', fontSize: 11, background: 'none', border: `1px solid ${borderColor}`, borderRadius: 4, padding: '0 8px', cursor: 'pointer', color: accentColor }}
        >↻</button>
      </div>
      <div style={{
        background: isDarkMode ? '#23252a' : '#f4f4f2', borderRadius: 12, padding: '18px 20px', minHeight: 64,
        fontFamily: '"Courier New", monospace', fontSize: '1.05em', fontWeight: 700, lineHeight: 1.6, color: isDarkMode ? '#e8e4db' : '#111',
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>
        <span>{shown}</span>
        <span style={{
          display: 'inline-block', width: 2, height: '1.2em', background: isDarkMode ? '#e8e4db' : '#111',
          verticalAlign: 'text-bottom', marginLeft: 1,
          animation: typing ? 'none' : 'outline-caret-blink 1s steps(1) infinite',
        }} />
      </div>
    </div>
  );
}

export default function OutlineModal({ verses, bookName, chapter, totalChapters, onPrevChapter, onNextChapter, onClose, isDarkMode, isSepiaMode, kjvContentRef, primaryPaneRef, precomputedOutline, suppressEscape, onNavigateRef, onOpenStory, onOpenCommentary, onOpenIntro, onOpenMemorize }) {
  const [showTags] = useState(false);
  // Type mode: after the outline sits still for 10s, type out the sentence nearest the top underneath itself
  const [typeMode, setTypeMode] = useState(() => {
    try { return localStorage.getItem('outline-type-mode') !== '0'; } catch (e) { return true; }
  });
  const [typeNodes, setTypeNodes] = useState(() => new Set()); // sentences currently showing a typewriter box (any number)
  const lastScrollAtRef = useRef(0); // hovers caused by content scrolling under a still pointer are ignored
  const hoverTypeNode = (node) => { if (Date.now() - lastScrollAtRef.current > 400) addTypeNode(node); };
  const addTypeNode = (node) => setTypeNodes(prev => (prev.has(node) ? prev : new Set(prev).add(node)));
  const clearTypeNodes = () => setTypeNodes(prev => (prev.size ? new Set() : prev));
  const [useAI, setUseAI] = useState(true);
  const [flatMode, setFlatMode] = useState(true);
  const [fz, setFz] = useState(() => {
    try { return parseFloat(localStorage.getItem('outline-fz')) || 1.0; } catch (e) { return 1.0; }
  });
  const [gameWordIdx, setGameWordIdx] = useState(null);
  const treeRef = useRef(null);
  const activeWordRef = useRef(null);
  const nodeElsRef = useRef(new Map());
  const onNodeRef = (node, el) => { nodeElsRef.current.set(node, el); };

  // Measure pane 2's on-screen rect so the outline swaps in over it exactly,
  // instead of covering the whole viewport as a floating modal.
  const [paneRect, setPaneRect] = useState(null);
  useEffect(() => {
    const el = kjvContentRef?.current;
    if (!el) return;
    const update = () => setPaneRect(el.getBoundingClientRect());
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, [kjvContentRef]);

  // Build one "paragraph" per verse — no verse numbers
  const paragraphs = useMemo(() => {
    if (!verses || !verses.length) return [];
    return verses
      .map(v => {
        const text = typeof v === 'string' ? v : (v.text || v.verse || String(v));
        return text.trim();
      })
      .filter(p => p);
  }, [verses]);

  // First verse visible at the top of pane 1 (falls back to the chapter's first verse) for the typewriter line
  const [firstSeen, setFirstSeen] = useState({ text: '', label: '' });
  useEffect(() => {
    const pane = primaryPaneRef?.current;
    let found = null;
    if (pane) {
      const top = pane.getBoundingClientRect().top;
      for (let n = 1; n <= 200; n++) {
        const el = document.getElementById(`verse-${n}`);
        if (!el) break;
        if (el.getBoundingClientRect().bottom > top + 10) {
          const span = el.querySelector('p > span.flex-1');
          const t = span ? span.textContent.trim() : '';
          if (t) { found = { text: t, label: `${bookName} ${chapter}:${n}` }; break; }
        }
      }
    }
    if (!found && paragraphs.length) found = { text: paragraphs[0], label: `${bookName} ${chapter}:1` };
    setFirstSeen(found || { text: '', label: '' });
    // Re-run only when the chapter changes, not on every pane-1 scroll
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter, bookName, verses]);

  // Sentence nearest the top of the outline viewport (first one still at least partly in view)
  const pickTopNode = () => {
    const el = treeRef.current;
    if (!el) return null;
    const top = el.getBoundingClientRect().top;
    let best = null, bestTop = Infinity;
    for (const [node, nodeEl] of nodeElsRef.current) {
      if (!nodeEl.isConnected) continue;
      const rect = nodeEl.getBoundingClientRect();
      if (rect.bottom > top + 8 && rect.top < bestTop) { best = node; bestTop = rect.top; }
    }
    return best;
  };

  useEffect(() => {
    try { localStorage.setItem('outline-type-mode', typeMode ? '1' : '0'); } catch (e) { /* ignore */ }
    clearTypeNodes();
    const el = treeRef.current;
    if (!typeMode || !el) return undefined;
    let timer = null;
    const arm = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const best = pickTopNode();
        if (best) addTypeNode(best);
      }, 10000);
    };
    const onScroll = () => { lastScrollAtRef.current = Date.now(); arm(); };
    arm(); // also start counting when the outline opens / the chapter changes, before any scroll
    el.addEventListener('scroll', onScroll);
    return () => { el.removeEventListener('scroll', onScroll); clearTimeout(timer); };
  }, [typeMode, chapter]);

  const aiRoots = useMemo(() => {
    if (!precomputedOutline?.outline) return null;
    return parseAIOutline(precomputedOutline.outline);
  }, [precomputedOutline]);

  const autoRoots = useMemo(() => outlineChapter(paragraphs), [paragraphs]);
  const flatRoots = useMemo(() => flatChapter(paragraphs), [paragraphs]);

  const roots = flatMode ? flatRoots : ((useAI && aiRoots) ? aiRoots : autoRoots);

  // ── Word-hop game: flatten all words across the outline tree ─────────────────
  const { flatWords, flatNodes, nodeWordRanges } = useMemo(() => {
    const fw = [];
    const fn = []; // [{start, count}]
    const nwr = new Map(); // node → {start, count}

    function traverse(nodes) {
      for (const node of nodes) {
        if (node.tag === 'spacer') continue;
        const words = node.text.match(/\S+/g) || [];
        if (words.length > 0) {
          const start = fw.length;
          nwr.set(node, { start, count: words.length });
          fn.push({ start, count: words.length });
          fw.push(...words);
        }
        if (node.children.length) traverse(node.children);
      }
    }

    traverse(roots);
    return { flatWords: fw, flatNodes: fn, nodeWordRanges: nwr };
  }, [roots]);

  // Reset game and scroll to top when chapter changes
  useEffect(() => {
    setGameWordIdx(null);
    if (treeRef.current) treeRef.current.scrollTop = 0;
  }, [chapter]);

  // Scroll active word into view
  useEffect(() => {
    if (gameWordIdx !== null && activeWordRef.current) {
      activeWordRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [gameWordIdx]);

  // Full-page scroll on wheel inside the outline tree — accumulates ticks so rapid scrolling stacks
  const scrollTargetRef = useRef(null);
  const scrollResetTimerRef = useRef(null);
  useEffect(() => {
    const el = treeRef.current;
    if (!el) return;
    // Trackpads emit a stream of wheel events (plus momentum) per swipe. Treat a swipe as one
    // gesture: page once at its start, ignore the rest until the stream pauses or a new push begins.
    let lastTime = 0;
    let lastAbs = 0;
    const onWheel = (e) => {
      e.preventDefault();
      const now = e.timeStamp;
      const abs = Math.abs(e.deltaY);
      const isNewGesture = now - lastTime > 120 || abs > lastAbs + 4;
      lastTime = now;
      lastAbs = abs;
      if (!isNewGesture || abs === 0) return;
      const page = el.clientHeight;
      // Use tracked target if still animating, otherwise start from actual scrollTop
      const current = scrollTargetRef.current ?? el.scrollTop;
      const next = Math.max(0, Math.min(el.scrollHeight - el.clientHeight, current + (e.deltaY > 0 ? page : -page)));
      scrollTargetRef.current = next;
      el.scrollTo({ top: next, behavior: 'smooth' });
      // Reset tracker after animation settles so next scroll starts from real position
      clearTimeout(scrollResetTimerRef.current);
      scrollResetTimerRef.current = setTimeout(() => { scrollTargetRef.current = null; }, 600);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      clearTimeout(scrollResetTimerRef.current);
    };
  }, []);

  // Keyboard navigation: Escape closes/deactivates game, ArrowLeft/Right navigate chapters, j/k/l word-hop game
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (gameWordIdx !== null) {
          e.preventDefault();
          setGameWordIdx(null);
          return; // deactivate game, don't close modal
        }
        if (!suppressEscape) {
          e.preventDefault();
          onClose();
        }
        return;
      }
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft') {
        if (chapter > 1) { e.preventDefault(); onPrevChapter(); if (treeRef.current) treeRef.current.scrollTop = 0; }
      } else if (e.key === 'ArrowRight') {
        if (chapter < totalChapters) { e.preventDefault(); onNextChapter(); if (treeRef.current) treeRef.current.scrollTop = 0; }
      }
      // Cross-navigation keys — mirror the header's Story/Intro/Memorize buttons
      else if (e.key === 'i' && onOpenMemorize) {
        e.preventDefault();
        onOpenMemorize();
      } else if (e.key === 't' && onOpenStory) {
        e.preventDefault();
        onOpenStory();
      } else if (e.key === 'n' && onOpenIntro) {
        e.preventDefault();
        onOpenIntro();
      }
      // Word-hop game keys
      else if (e.key === 'j') {
        e.preventDefault();
        setGameWordIdx(prev => {
          if (prev !== null) return Math.min(prev + 1, flatWords.length - 1);
          // Find first visible node in the scroll container
          const container = treeRef.current;
          if (container) {
            const cTop = container.getBoundingClientRect().top;
            const cBottom = container.getBoundingClientRect().bottom;
            for (const [node, range] of nodeWordRanges) {
              const el = nodeElsRef.current.get(node);
              if (el) {
                const rect = el.getBoundingClientRect();
                if (rect.bottom > cTop && rect.top < cBottom) return range.start;
              }
            }
          }
          return 0;
        });
      } else if (e.key === 'l') {
        e.preventDefault();
        if (gameWordIdx !== null) setGameWordIdx(null);
        const el = treeRef.current;
        if (el) el.scrollBy({ top: -(el.clientHeight - 60), behavior: 'smooth' });
      } else if (e.key === 'h') {
        e.preventDefault();
        if (gameWordIdx !== null) setGameWordIdx(null);
        const el = treeRef.current;
        if (el) el.scrollBy({ top: el.clientHeight - 60, behavior: 'smooth' });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, suppressEscape, chapter, totalChapters, onPrevChapter, onNextChapter, gameWordIdx, flatWords, flatNodes, nodeWordRanges, onOpenMemorize, onOpenStory, onOpenIntro]);

  const bg = isDarkMode ? '#1a1c20' : '#faf8f3';
  const textColor = isDarkMode ? '#e8e4db' : '#2b2b2b';
  const borderColor = isDarkMode ? '#3a3a4a' : '#e3e0d8';
  const accentColor = isDarkMode ? '#a08060' : '#7a5c2e';
  const typeProps = { isDarkMode, accentColor, borderColor };
  const navBtnStyle = (disabled) => ({
    fontFamily: 'inherit', fontSize: 13, background: 'none', border: `1px solid ${borderColor}`,
    borderRadius: 4, padding: '2px 10px', cursor: disabled ? 'default' : 'pointer',
    color: disabled ? '#aaa' : accentColor, opacity: disabled ? 0.4 : 1,
  });

  function handleModalKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault(); e.nativeEvent?.stopImmediatePropagation();
      const el = treeRef.current;
      if (el) el.scrollBy({ top: el.clientHeight - 60, behavior: 'smooth' });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); e.nativeEvent?.stopImmediatePropagation();
      const el = treeRef.current;
      if (el) el.scrollBy({ top: -(el.clientHeight - 60), behavior: 'smooth' });
    } else if (e.key === 'r' && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const sel = window.getSelection();
      const text = sel ? sel.toString().trim() : '';
      if (!text) return;
      e.preventDefault();
      window.speechSynthesis.cancel();
      const utt = new SpeechSynthesisUtterance(text);
      utt.lang = 'en-US';
      const voices = window.speechSynthesis.getVoices();
      const voice =
        voices.find(v => v.lang.startsWith('en') && v.name.includes('Google')) ||
        voices.find(v => v.lang.startsWith('en-US') && (v.name.includes('Enhanced') || v.name.includes('Premium'))) ||
        voices.find(v => v.lang.startsWith('en-US')) ||
        voices.find(v => v.lang.startsWith('en')) || null;
      if (voice) utt.voice = voice;
      window.speechSynthesis.speak(utt);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: paneRect ? paneRect.top : 0,
        left: paneRect ? paneRect.left : 0,
        width: paneRect ? paneRect.width : '100%',
        height: paneRect ? paneRect.height : '100%',
        visibility: paneRect ? 'visible' : 'hidden',
        background: bg, color: textColor, display: 'flex', flexDirection: 'column',
        fontFamily: "Georgia, 'Times New Roman', serif", overflow: 'hidden', zIndex: 100,
      }}
      onKeyDown={handleModalKeyDown}
      tabIndex={-1}
    >
      {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'monospace', fontSize: 12, color: accentColor, fontWeight: 700, letterSpacing: '0.05em' }}>OUTLINE</span>
          {gameWordIdx !== null && (
            <span style={{ fontFamily: 'monospace', fontSize: 10, padding: '2px 7px', borderRadius: 9, background: '#3b82f6', color: '#fff', fontWeight: 700 }}
              title="Word-hop game active — j/k=word, l/;=sentence, Esc=exit">
              {gameWordIdx + 1}/{flatWords.length}
            </span>
          )}
          {aiRoots && !flatMode && (
            <button
              onClick={() => setUseAI(v => !v)}
              style={{ fontFamily: 'monospace', fontSize: 10, padding: '2px 7px', borderRadius: 9, border: 'none', cursor: 'pointer', background: useAI ? accentColor : (isDarkMode ? '#3a3a4a' : '#e3e0d8'), color: useAI ? '#fff' : (isDarkMode ? '#aaa' : '#666'), fontWeight: 700, letterSpacing: '0.04em' }}
              title={useAI ? 'Showing AI outline — click for auto' : 'Showing auto outline — click for AI'}
            >{useAI ? 'AI' : 'AUTO'}</button>
          )}
          <button
            onClick={() => setFlatMode(v => !v)}
            style={{ fontFamily: 'monospace', fontSize: 10, padding: '2px 7px', borderRadius: 9, border: 'none', cursor: 'pointer', background: flatMode ? '#374151' : (isDarkMode ? '#3a3a4a' : isSepiaMode ? '#c8b89a' : '#e3e0d8'), color: flatMode ? '#fff' : (isDarkMode ? '#aaa' : isSepiaMode ? '#5a4a35' : '#666'), fontWeight: 700, letterSpacing: '0.04em' }}
            title={flatMode ? 'Flat bullets — click for outline' : 'Outline — click for flat bullets'}
          >{flatMode ? 'FLAT ON' : 'FLAT'}</button>
          <button
            onClick={() => { if (typeNodes.size) clearTypeNodes(); else { const n = pickTopNode(); if (n) addTypeNode(n); } }}
            style={{ fontFamily: 'monospace', fontSize: 11, padding: '1px 7px', borderRadius: 9, border: `1px solid ${borderColor}`, cursor: 'pointer', background: 'none', color: accentColor, fontWeight: 700 }}
            title="Type the sentence nearest the top now — or, if boxes are open, close them all"
          >⌨</button>
          <button
            onClick={() => setTypeMode(v => !v)}
            style={{ fontFamily: 'monospace', fontSize: 10, padding: '2px 7px', borderRadius: 9, border: 'none', cursor: 'pointer', background: typeMode ? '#374151' : (isDarkMode ? '#3a3a4a' : isSepiaMode ? '#c8b89a' : '#e3e0d8'), color: typeMode ? '#fff' : (isDarkMode ? '#aaa' : isSepiaMode ? '#5a4a35' : '#666'), fontWeight: 700, letterSpacing: '0.04em' }}
            title={typeMode ? 'Typewriter on — types the sentence near the top after 10s of no scrolling. Click to turn off' : 'Typewriter off — click to type the sentence near the top after 10s of no scrolling'}
          >{typeMode ? 'TYPE ON' : 'TYPE'}</button>
          {/* Prev / chapter title / Next */}
          <button onClick={() => { onPrevChapter(); if (treeRef.current) treeRef.current.scrollTop = 0; }} disabled={chapter <= 1} style={navBtnStyle(chapter <= 1)}>‹</button>
          <span style={{ fontWeight: 700, fontSize: 15, color: textColor }}>{bookName} {chapter}</span>
          <button onClick={() => { onNextChapter(); if (treeRef.current) treeRef.current.scrollTop = 0; }} disabled={chapter >= totalChapters} style={navBtnStyle(chapter >= totalChapters)}>›</button>

          {/* Cross-navigation buttons */}
          {onOpenStory && (
            <button
              onClick={onOpenStory}
              style={{ fontFamily: 'inherit', fontSize: 13, background: 'none', border: `1px solid ${borderColor}`, borderRadius: 4, padding: '2px 8px', cursor: 'pointer', color: accentColor, whiteSpace: 'nowrap' }}
              title="Open story"
            >S(t)ory</button>
          )}
          {onOpenCommentary && (
            <button
              onClick={onOpenCommentary}
              style={{ fontFamily: 'inherit', fontSize: 13, background: 'none', border: `1px solid ${borderColor}`, borderRadius: 4, padding: '2px 8px', cursor: 'pointer', color: accentColor, whiteSpace: 'nowrap' }}
              title="Open commentary"
            >commen</button>
          )}

          <span style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
            <button onClick={() => setFz(f => { const n = Math.max(0.7, +(f - 0.1).toFixed(2)); try { localStorage.setItem('outline-fz', n); } catch (e) {} return n; })} style={{ fontFamily: 'inherit', fontSize: 12, background: 'none', border: 'none', cursor: 'pointer', color: accentColor, padding: '2px 2px' }}>A−</button>
            <button onClick={() => setFz(f => { const n = Math.min(2.0, +(f + 0.1).toFixed(2)); try { localStorage.setItem('outline-fz', n); } catch (e) {} return n; })} style={{ fontFamily: 'inherit', fontSize: 12, background: 'none', border: 'none', cursor: 'pointer', color: accentColor, padding: '2px 2px' }}>A+</button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#888', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
          </span>
        </div>

        {/* Tree */}
        <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <button
            onClick={() => { if (treeRef.current) treeRef.current.scrollBy({ top: -(treeRef.current.clientHeight - 60), behavior: 'smooth' }); }}
            style={{ position: 'absolute', bottom: 62, left: 14, zIndex: 10, fontFamily: 'inherit', fontSize: 18, background: isDarkMode ? '#2a2c30' : '#fff', border: `1px solid ${borderColor}`, borderRadius: 6, padding: '4px 12px', cursor: 'pointer', color: accentColor, boxShadow: '0 2px 6px rgba(0,0,0,0.18)', opacity: 0.92 }}
            title="Scroll up (same as the up arrow key)"
          >↑</button>
          <button
            onClick={() => { if (treeRef.current) treeRef.current.scrollBy({ top: treeRef.current.clientHeight * 0.8, behavior: 'smooth' }); }}
            style={{ position: 'absolute', bottom: 14, left: 14, zIndex: 10, fontFamily: 'inherit', fontSize: 18, background: isDarkMode ? '#2a2c30' : '#fff', border: `1px solid ${borderColor}`, borderRadius: 6, padding: '4px 12px', cursor: 'pointer', color: accentColor, boxShadow: '0 2px 6px rgba(0,0,0,0.18)', opacity: 0.92 }}
            title="Page down"
          >↓</button>
          <div ref={treeRef} id="outline-tree-scroll" className={isDarkMode ? 'scrollbar-dark' : isSepiaMode ? 'scrollbar-sepia' : ''} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 28px 32px', fontSize: `${fz}rem`, textAlign: 'left', scrollbarColor: isDarkMode ? '#555 #2a2a2a' : isSepiaMode ? '#c4b89a #f4ecd8' : undefined }}>
            {typeMode && <TypewriterLine text={firstSeen.text} label={firstSeen.label} isDarkMode={isDarkMode} accentColor={accentColor} borderColor={borderColor} />}
            {roots.length === 0 ? (
              <p style={{ color: '#888', fontStyle: 'italic' }}>No verses to outline.</p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {roots.map((node, i) => (
                  <OutlineNodeEl key={i} node={node} showTags={showTags} gameWordIdx={gameWordIdx} nodeWordRanges={nodeWordRanges} activeWordRef={activeWordRef} onNodeRef={onNodeRef} typeNodes={typeNodes} typeProps={typeProps} onHoverNode={typeMode ? hoverTypeNode : undefined} />
                ))}
              </ul>
            )}
            {chapter < totalChapters && (
              <div style={{ marginTop: 24, textAlign: 'left' }}>
                <button
                  onClick={() => { onNextChapter(); if (treeRef.current) treeRef.current.scrollTop = 0; }}
                  style={{ ...navBtnStyle(false), fontSize: 17, padding: '6px 18px' }}
                >
                  Next Chapter ›
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
  );
}
